import { create } from 'zustand';
import * as OrgAdminService from '@services/OrgAdminService';
import {
  OrgAdmin,
  User,
  Domain,
  CreateOrgAdminRequest,
  CreateUserAndOrgAdminRequest,
  UpdateOrgAdminRequest,
  OrgAdminDetails,
} from '@services/OrgAdminService';

interface OrgAdminState {
  // Data
  admins: OrgAdmin[];
  users: User[];
  domains: Domain[];
  currentAdmin: OrgAdmin | null;
  orgDetails: OrgAdminDetails | null;

  // UI States
  loading: boolean;
  error: string | null;
  success: string | null;

  // Actions
  fetchAllAdmins: () => Promise<void>;
  fetchAllUsers: () => Promise<void>;
  fetchAllDomains: () => Promise<void>;
  fetchOrgAdminDetails: (id: string | number) => Promise<void>;
  createAdmin: (data: CreateOrgAdminRequest) => Promise<boolean>;
  createUserAndAdmin: (data: CreateUserAndOrgAdminRequest) => Promise<boolean>;
  updateAdmin: (id: number, data: UpdateOrgAdminRequest) => Promise<boolean>;
  deleteAdmin: (id: number) => Promise<boolean>;
  setCurrentAdmin: (admin: OrgAdmin | null) => void;
  clearMessages: () => void;
  resetStore: () => void;
}

const initialState = {
  admins: [],
  users: [],
  domains: [],
  currentAdmin: null,
  orgDetails: null,
  loading: false,
  error: null,
  success: null,
};

export const useOrgAdminStore = create<OrgAdminState>((set, get) => ({
  ...initialState,

  /**
   * Fetch all organizational admins
   */
  fetchAllAdmins: async () => {
    set({ loading: true, error: null });

    const response = await OrgAdminService.getAllOrgAdmins();

    if (response.success && response.data) {
      set({
        admins: response.data,
        loading: false,
        error: null,
      });
    } else {
      set({
        admins: [],
        loading: false,
        error: response.error as string || 'Failed to fetch organizational admins',
      });
    }
  },

  /**
   * Fetch all users for dropdown
   */
  fetchAllUsers: async () => {
    set({ loading: true, error: null });

    const response = await OrgAdminService.getAllUsers();

    if (response.success && response.data) {
      set({
        users: response.data,
        loading: false,
        error: null,
      });
    } else {
      set({
        users: [],
        loading: false,
        error: response.error as string || 'Failed to fetch users',
      });
    }
  },

  /**
   * Fetch all unique domains for dropdown
   */
  fetchAllDomains: async () => {
    set({ loading: true, error: null });

    const response = await OrgAdminService.getAllDomains();

    if (response.success && response.data) {
      set({
        domains: response.data,
        loading: false,
        error: null,
      });
    } else {
      set({
        domains: [],
        loading: false,
        error: response.error as string || 'Failed to fetch domains',
      });
    }
  },

  /**
   * Fetch organization admin details with stats
   */
  fetchOrgAdminDetails: async (id: string | number) => {
    set({ loading: true, error: null, orgDetails: null });

    const response = await OrgAdminService.getOrgAdminDetails(id);

    if (response.success && response.data) {
      set({
        orgDetails: response.data,
        loading: false,
        error: null,
      });
    } else {
      set({
        orgDetails: null,
        loading: false,
        error: response.error as string || 'Failed to fetch organization details',
      });
    }
  },

  /**
   * Create a new organizational admin
   */
  createAdmin: async (data: CreateOrgAdminRequest): Promise<boolean> => {
    set({ loading: true, error: null, success: null });

    const response = await OrgAdminService.createOrgAdmin(data);

    if (response.success) {
      set({
        loading: false,
        error: null,
        success: 'Organizational admin created successfully',
      });

      // Refresh the admins list
      get().fetchAllAdmins();

      return true;
    } else {
      set({
        loading: false,
        error: response.error as string || 'Failed to create organizational admin',
        success: null,
      });

      return false;
    }
  },

  /**
   * Create new user and organizational admin
   */
  createUserAndAdmin: async (data: CreateUserAndOrgAdminRequest): Promise<boolean> => {
    set({ loading: true, error: null, success: null });

    const response = await OrgAdminService.createUserAndOrgAdmin(data);

    if (response.success) {
      set({
        loading: false,
        error: null,
        success: 'User and organizational admin created successfully. An email with login credentials has been sent.',
      });

      // Refresh the admins list and users list
      get().fetchAllAdmins();
      get().fetchAllUsers();

      return true;
    } else {
      set({
        loading: false,
        error: response.error as string || 'Failed to create user and organizational admin',
        success: null,
      });

      return false;
    }
  },

  /**
   * Update an organizational admin
   */
  updateAdmin: async (id: number, data: UpdateOrgAdminRequest): Promise<boolean> => {
    set({ loading: true, error: null, success: null });

    const response = await OrgAdminService.updateOrgAdmin(id, data);

    if (response.success) {
      set({
        loading: false,
        error: null,
        success: 'Organizational admin updated successfully',
      });

      // Refresh the admins list
      get().fetchAllAdmins();

      return true;
    } else {
      set({
        loading: false,
        error: response.error as string || 'Failed to update organizational admin',
        success: null,
      });

      return false;
    }
  },

  /**
   * Delete an organizational admin
   */
  deleteAdmin: async (id: number): Promise<boolean> => {
    set({ loading: true, error: null, success: null });

    const response = await OrgAdminService.removeOrgAdmin(id);

    if (response.success) {
      set({
        loading: false,
        error: null,
        success: 'Organizational admin removed successfully',
      });

      // Refresh the admins list
      get().fetchAllAdmins();

      return true;
    } else {
      set({
        loading: false,
        error: response.error as string || 'Failed to remove organizational admin',
        success: null,
      });

      return false;
    }
  },

  /**
   * Set current admin for editing
   */
  setCurrentAdmin: (admin: OrgAdmin | null) => {
    set({ currentAdmin: admin });
  },

  /**
   * Clear success and error messages
   */
  clearMessages: () => {
    set({ error: null, success: null });
  },

  /**
   * Reset store to initial state
   */
  resetStore: () => {
    set(initialState);
  },
}));
